import { companyProfileSchema, logoUploadResponseSchema } from '@houzy/schemas/partner-cockpit/company';
import {
    queryOptions,
    useMutation,
    useQuery,
    useQueryClient,
    type UseMutationResult,
    type UseQueryResult,
} from '@tanstack/react-query';

import { apiRequest } from '../../../lib/api';
import { partnerCockpitApiPath } from '../../../lib/config';
import { STALE_TIME_MS } from '../../../lib/queryClient';
import type { CompanyProfile } from '../types';
import { companyLocationQueryOptions } from './workRegionsApi';

export async function fetchCompanyProfile(signal?: AbortSignal): Promise<CompanyProfile> {
    return apiRequest(partnerCockpitApiPath('company/profile'), { signal, responseSchema: companyProfileSchema });
}

export async function uploadCompanyLogo(file: File): Promise<string> {
    const form = new FormData();
    form.append('logo', file);
    const result = await apiRequest(partnerCockpitApiPath('company/profile/logo'), {
        method: 'POST',
        body: form,
        responseSchema: logoUploadResponseSchema,
    });
    return result.logoUrl;
}

export async function saveCompanyProfile(profile: CompanyProfile): Promise<void> {
    await apiRequest(partnerCockpitApiPath('company/profile'), {
        method: 'PUT',
        body: {
            companyName: profile.companyName,
            description: profile.description,
            street: profile.street,
            nr: profile.nr,
            city: profile.city,
            postcode: profile.postcode,
            website: profile.website,
            employees: profile.employees,
            logoUrl: profile.logoUrl,
        },
    });
}

export const companyProfileQueryKey = ['settings', 'companyProfile'] as const;

export const companyProfileQueryOptions = queryOptions({
    queryKey: companyProfileQueryKey,
    queryFn: ({ signal }) => fetchCompanyProfile(signal),
    staleTime: STALE_TIME_MS,
});

export function useCompanyProfile(): UseQueryResult<CompanyProfile> {
    return useQuery(companyProfileQueryOptions);
}

export function useSaveCompanyProfile(): UseMutationResult<void, Error, CompanyProfile> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['settings', 'save', 'companyProfile'],
        mutationFn: saveCompanyProfile,
        onSuccess: async (_, profile) => {
            queryClient.setQueryData<CompanyProfile>(companyProfileQueryKey, profile);
            // Saving re-geocodes the address, which is what the work-region map pins and the distance filter
            // measures from, so a partner who just added their address sees the slider come alive.
            await queryClient.invalidateQueries({ queryKey: companyLocationQueryOptions.queryKey });
            await queryClient.invalidateQueries({ queryKey: ['settings', 'postalCodesInRange'] });
        },
    });
}
